// Опис структури товарно-транспортної накладної (форма N 1-ТН, Додаток 7),
// узятий із public/templates/ttn.xlsx (аркуш TDSheet, A1:BS63).
//
// Це єдине джерело правди і для форми (components/TtnForm.tsx), і для
// генератора xlsx (utils/ttnXlsx.ts): кожне поле має ключ, укр. підпис та
// адресу «якірної» комірки шаблону, куди підставляється значення.

export type TtnFieldKind = 'text' | 'textarea' | 'number' | 'date';

export type TtnFieldDef = {
    key: string;
    label: string;
    /** Адреса комірки-якоря у шаблоні (верхній-лівий кут об'єднаного діапазону). */
    cell: string;
    kind?: TtnFieldKind;
    hint?: string;
    /** Значення за замовчуванням для нової ТТН. */
    def?: string;
};

export type TtnSection = {
    title: string;
    fields: TtnFieldDef[];
    /** Спеціальний блок, який рендериться одразу ПІСЛЯ цієї секції
     *  (для збереження порядку елементів як у бланку ttn.xlsx). */
    blockAfter?: 'cargo';
};

// --- Шапка + підсумки (усе, крім таблиці «ВІДОМОСТІ ПРО ВАНТАЖ»). --------------
// Комбіновані поля (напр. «найменування + код ЄДРПОУ») збираються у рядок
// генератором xlsx — див. buildCombinedValues() у ttnXlsx.ts.
//
// Порядок секцій і полів відтворює послідовність бланку ttn.xlsx (аркуш
// TDSheet, згори вниз), а `hint` кожного поля — це підпис-пояснення, який у
// бланку надрукований дрібним шрифтом під відповідною коміркою (рядки 12, 15,
// 18, 21, 24, 27, 30, 33, 36, 47) або відповідна виноска (*, **, ***
// у рядках 59/61/63).

// Повторюваний у бланку абзац-пояснення для «найменування» учасника перевезення
// (комірки G18 / G21 / G24 у ttn.xlsx).
const FULL_NAME_HINT =
    'повне найменування (прізвище (за наявності), власне ім\'я та по батькові ' +
    '(за наявності), унікальний номер запису в Єдиному державному демографічному ' +
    'реєстрі (за наявності), код платника податків згідно з Єдиним державним ' +
    'реєстром підприємств та організацій України або податковий номер ' +
    '(реєстраційний номер облікової картки платника податків або серія ' +
    '(за наявності) та номер паспорта громадянина України (для фізичних осіб, ' +
    'які через свої релігійні переконання відмовляються від прийняття ' +
    'реєстраційного номера облікової картки платника податків та повідомили про ' +
    'це відповідний контролюючий орган і мають відмітку в паспорті))';

export const TTN_SECTIONS: TtnSection[] = [
    {
        title: 'Общие сведения',
        fields: [
            { key: 'number', label: '№ накладної', cell: 'AD7' },
            { key: 'date', label: 'Дата складання', cell: 'AJ7', kind: 'date' },
            { key: 'placeOfCompilation', label: 'Місце складання', cell: 'M9' },
        ],
    },
    {
        title: 'Транспортное средство',
        fields: [
            {
                key: 'vehicle', label: 'Автомобіль', cell: 'J11',
                hint: 'марка, модель, тип, реєстраційний номер',
            },
            {
                key: 'trailer', label: 'Причіп / напівпричіп', cell: 'AJ11',
                hint: 'марка, модель, тип, реєстраційний номер',
            },
            {
                key: 'carriageType', label: 'Вид перевезень', cell: 'BG11',
                def: 'По кілометровому тарифу',
            },
            {
                key: 'vehicleStorage', label: 'Місце де зберігається автомобіль *', cell: 'Q14',
                kind: 'textarea',
                hint: 'адреса місцезнаходження автомобільного перевізника, його ' +
                    'структурного підрозділу або філії, де зберігається транспортний ' +
                    'засіб. * Відомості заповнюються у випадках, передбачених ' +
                    'Положенням про робочий час і час відпочинку водіїв колісних ' +
                    'транспортних засобів, затвердженим наказом Міністерства ' +
                    'транспорту та зв\'язку України від 07 червня 2010 року № 340',
            },
        ],
    },
    {
        title: 'Участники перевозки',
        fields: [
            {
                key: 'carrier', label: 'Автомобільний перевізник', cell: 'O17',
                kind: 'textarea', hint: FULL_NAME_HINT,
            },
            {
                key: 'driverName', label: 'Водій', cell: 'AX17',
                hint: 'прізвище (за наявності), власне ім\'я та по батькові ' +
                    '(за наявності), унікальний номер запису в Єдиному державному ' +
                    'демографічному реєстрі (за наявності)',
            },
            {
                key: 'driverLicence', label: 'Водій — № посвідчення', cell: '',
                hint: 'номер посвідчення водія',
            },
            {
                key: 'shipperName', label: 'Вантажовідправник', cell: 'M20',
                hint: FULL_NAME_HINT,
            },
            { key: 'shipperCode', label: 'Вантажовідправник — код ЄДРПОУ', cell: '' },
            {
                key: 'consigneeName', label: 'Вантажоодержувач', cell: 'M23',
                hint: FULL_NAME_HINT,
            },
            { key: 'consigneeCode', label: 'Вантажоодержувач — код ЄДРПОУ', cell: '' },
        ],
    },
    {
        title: 'Пункты погрузки / разгрузки',
        fields: [
            {
                key: 'loadingPoint', label: 'Пункт навантаження', cell: 'M26',
                kind: 'textarea', hint: 'місцезнаходження',
            },
            {
                key: 'unloadingPoint', label: 'Пункт розвантаження', cell: 'AU26',
                kind: 'textarea', hint: 'місцезнаходження',
            },
        ],
    },
    {
        title: 'Приём-передача груза',
        fields: [
            { key: 'placesCount', label: 'Кількість місць', cell: 'K29', hint: 'словами' },
            { key: 'grossWeightWords', label: 'Масою брутто, т', cell: 'AF29', hint: 'словами' },
            {
                key: 'receivedByDriver', label: 'Отримав водій / експедитор', cell: 'AZ29',
                hint: 'прізвище (за наявності), власне ім\'я та по батькові ' +
                    '(за наявності), унікальний номер запису в Єдиному державному ' +
                    'демографічному реєстрі (за наявності), посада, підпис',
            },
        ],
    },
    {
        title: 'Сведения о транспортном средстве (автомобиль / автопоезд / комбинированное транспортное средство)',
        fields: [
            { key: 'dimLength', label: 'Довжина, м', cell: 'AD32', def: '0', hint: 'довжина, м' },
            { key: 'dimWidth', label: 'Ширина, м', cell: 'AJ32', def: '0', hint: 'ширина, м' },
            { key: 'dimHeight', label: 'Висота, м', cell: 'AQ32', def: '0', hint: 'висота, м' },
            {
                key: 'dimWeight', label: 'Загальна вага / маса з вантажем, т', cell: 'AZ32',
                def: '0 / 0', hint: 'загальна вага / маса з вантажем та маса брутто, т',
            },
        ],
    },
    {
        title: 'Суммы и документы',
        blockAfter: 'cargo',
        fields: [
            {
                key: 'totalSumWords', label: 'Усього відпущено на загальну суму', cell: 'Q35',
                hint: 'словами, з урахуванням ПДВ (заповнюється автоматично з таблиці ' +
                    'вантажу, можна змінити вручну)',
            },
            { key: 'vatSum', label: 'У тому числі ПДВ', cell: 'BC35', def: '-' },
            {
                key: 'accompanyingDocs', label: 'Супровідні документи на вантаж', cell: 'Q38',
                kind: 'textarea', def: 'Видаткова накладна №  від',
            },
        ],
    },
    {
        title: 'Подписи сторон',
        fields: [
            {
                key: 'handedOver', label: 'Здав (відповідальна особа вантажовідправника)', cell: 'H46',
                hint: 'прізвище (за наявності), власне ім\'я та по батькові ' +
                    '(за наявності), посада, підпис',
            },
            {
                key: 'accepted', label: 'Прийняв (відповідальна особа вантажоодержувача)', cell: 'AM46',
                hint: 'прізвище (за наявності), власне ім\'я та по батькові ' +
                    '(за наявності), посада, підпис',
            },
        ],
    },
];

// Рядки таблиці «ВАНТАЖНО-РОЗВАНТАЖУВАЛЬНІ ОПЕРАЦІЇ» (шаблон: р.56 «Завантаження»,
// р.57 «Розвантаження»). Ключі -> адреси комірок ДО зсуву рядками вантажу.
export type TtnOperationDef = { key: string; label: string; cell: string };
export const TTN_OPERATION_ROWS: { title: string; cols: TtnOperationDef[] }[] = [
    {
        title: 'Погрузка',
        cols: [
            { key: 'loadGross', label: 'Маса брутто, т', cell: 'N56' },
            { key: 'loadArrival', label: 'Прибуття (год хв)', cell: 'Z56' },
            { key: 'loadDeparture', label: 'Вибуття (год хв)', cell: 'AK56' },
            { key: 'loadIdle', label: 'Простій (год хв)', cell: 'AW56' },
            { key: 'loadSign', label: 'Підпис відповідальної особи', cell: 'BH56' },
        ],
    },
    {
        title: 'Разгрузка',
        cols: [
            { key: 'unloadGross', label: 'Маса брутто, т', cell: 'N57' },
            { key: 'unloadArrival', label: 'Прибуття (год хв)', cell: 'Z57' },
            { key: 'unloadDeparture', label: 'Вибуття (год хв)', cell: 'AK57' },
            { key: 'unloadIdle', label: 'Простій (год хв)', cell: 'AW57' },
            { key: 'unloadSign', label: 'Підпис відповідальної особи', cell: 'BH57' },
        ],
    },
];

// --- Таблиця «ВІДОМОСТІ ПРО ВАНТАЖ» -----------------------------------------
// У шаблоні: р.41 — заголовки, р.42 — нумерація колонок «1..11», р.43 — «Усього:».
// Рядків даних немає — генератор вставляє їх МІЖ р.42 і р.43.

export const TTN_CARGO_HEADER_ROW = 41;
export const TTN_CARGO_NUMBERS_ROW = 42;
export const TTN_CARGO_TOTALS_ROW = 43;

// Об'єднання колонок одного рядка вантажу: [перша комірка, остання комірка].
export const TTN_CARGO_COL_SPANS: Array<[string, string]> = [
    ['D', 'E'],   // № з/п (автоматично)
    ['F', 'Q'],   // найменування вантажу
    ['R', 'U'],   // ідентиф. № тварини
    ['V', 'Z'],   // вид тварини
    ['AA', 'AD'], // температурний режим
    ['AE', 'AH'], // одиниця вимірювання
    ['AI', 'AM'], // кількість місць
    ['AN', 'AR'], // ціна без ПДВ за одиницю
    ['AS', 'AZ'], // загальна сума з ПДВ
    ['BA', 'BD'], // вид пакування
    ['BE', 'BN'], // документи з вантажем
    ['BO', 'BQ'], // маса брутто, т
];

export type TtnCargoColDef = {
    key: string;
    label: string;
    hint?: string;
    /** Числова колонка — сумується у рядок «Усього:». */
    numeric?: boolean;
    /** Автоматична (порядковий номер) — у формі не редагується. */
    auto?: boolean;
};

// Порядок відповідає TTN_CARGO_COL_SPANS. `hint` — повний текст заголовка
// відповідної колонки з рядка 41 бланку ttn.xlsx (з розшифровкою виносок
// **/*** із рядків 61/63).
export const TTN_CARGO_COLUMNS: TtnCargoColDef[] = [
    { key: 'idx', label: '№ з/п', auto: true },
    {
        key: 'name', label: 'Найменування вантажу',
        hint: 'Найменування вантажу (туші, напівтуші, четвертини, відруби, шматки ' +
            'м\'яса) ** / номер контейнера; клас небезпечних речовин, до якого ' +
            'віднесено вантаж, у разі перевезення небезпечних вантажів',
    },
    {
        key: 'animalId', label: 'Ідент. № тварини',
        hint: 'Ідентифікаційний номер тварини, від якої отримано сировину. ' +
            '** Заповнюється у разі перевезення туш, напівтуш, четвертин, ' +
            'відрубів, шматків м\'яса',
    },
    {
        key: 'animalType', label: 'Вид тварини',
        hint: 'Вид тварини. ** Заповнюється у разі перевезення туш, напівтуш, ' +
            'четвертин, відрубів, шматків м\'яса',
    },
    {
        key: 'tempMode', label: 'Темп. режим',
        hint: 'Температурний режим транспортування. *** Заповнюється у разі ' +
            'перевезення харчових продуктів, які потребують дотримання ' +
            'температурного режиму',
    },
    { key: 'unit', label: 'Од. вим.', hint: 'Одиниця вимірювання' },
    { key: 'places', label: 'К-ть місць', numeric: true, hint: 'Кількість місць' },
    { key: 'price', label: 'Ціна без ПДВ', numeric: true, hint: 'Ціна без ПДВ за одиницю, грн' },
    { key: 'sumWithVat', label: 'Сума з ПДВ', numeric: true, hint: 'Загальна сума з ПДВ, грн' },
    { key: 'packaging', label: 'Вид пакування', hint: 'Вид пакування' },
    { key: 'docs', label: 'Документи', hint: 'Документи з вантажем' },
    { key: 'grossWeight', label: 'Маса брутто, т', numeric: true, hint: 'Маса брутто, т' },
];

// Колонки рядка «Усього:», куди пишуться суми (адреси ДО зсуву).
export const TTN_CARGO_TOTALS_CELLS: { [key: string]: string } = {
    places: 'AI43',
    sumWithVat: 'AS43',
    grossWeight: 'BO43',
};

// --- Типи значень ----------------------------------------------------------

export type TtnCargoRow = { [key: string]: string };

export type TtnFields = {
    [key: string]: string | TtnCargoRow[] | undefined;
    cargo?: TtnCargoRow[];
};

export const emptyCargoRow = (): TtnCargoRow => {
    const r: TtnCargoRow = {};
    TTN_CARGO_COLUMNS.forEach((c) => { if (!c.auto) r[c.key] = ''; });
    return r;
};

// Початкові значення форми (з урахуванням def).
export const makeDefaultTtnFields = (): TtnFields => {
    const f: TtnFields = { cargo: [emptyCargoRow()] };
    TTN_SECTIONS.forEach((s) => s.fields.forEach((fd) => { f[fd.key] = fd.def ?? ''; }));
    TTN_OPERATION_ROWS.forEach((row) => row.cols.forEach((c) => { f[c.key] = ''; }));
    return f;
};

// Місяці у родовому відмінку для формату дати `"27" липня 2026 року`.
const UA_MONTHS_GEN = [
    'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
    'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
];

export const formatUaDate = (iso: string): string => {
    if (!iso) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!m) return iso;
    const [, y, mm, dd] = m;
    const monthIdx = parseInt(mm, 10) - 1;
    if (monthIdx < 0 || monthIdx > 11) return iso;
    return `"${dd}" ${UA_MONTHS_GEN[monthIdx]} ${y} року`;
};
